import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import { TowersHistory } from '../interfaces/towers-history.interface';
import { TableDateCellComponent } from '@core/components/table/table-date-cell/table-date-cell.component';
import { ClipboardService } from '@core/services/clipboard.service';
import { TableColorCellComponent } from '@core/components/table/table-color-cell/table-color-cell.component';
import { CostComponent } from '@core/components/cost/cost.component';
import { FairnessButtonComponent } from '@core/components/fairness-button/fairness-button.component';
import { ModalService } from '@features/modals/services/modal.service';

interface Props {
  copy: typeof ClipboardService.prototype.copy;
  modalService: ModalService;
}

export const towersHistoryTableConfig = (
  props: Props
): ColumnDef<TowersHistory>[] => [
  {
    accessorFn: (row) => row.id,
    id: 'id',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'ID',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.value,
    id: 'value',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Value',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: cell.getValue() as number,
          balanceType: 'skins',
          gap: 4,
          color: 'var(--yellow)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.tiles,
    id: 'tiles',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Tiles',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.death_tiles,
    id: 'death_tiles',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Death Tiles',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.time,
    id: 'time',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Time',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(TableDateCellComponent, {
        inputs: {
          value: (cell.getValue() as number) * 1000,
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.outcome,
    id: 'outcome',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Outcome',
      };
    },
    cell: (context) => {
      const isPositive = (context.getValue() as number) > 0;
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: context.getValue() as number,
          balanceType: 'skins',
          gap: 4,
          color: isPositive ? 'var(--green)' : 'var(--red)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.serialid,
    id: 'serialid',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Serial ID',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.client_seed,
    id: 'client_seed',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Client seed',
      };
    },
    cell: (cell) => {
      const clientSeed = cell.getValue() as string;

      return flexRenderComponent(TableColorCellComponent, {
        inputs: {
          value: clientSeed,
          maxWidth: 100,
          cursor: 'pointer',
        },
        outputs: {
          handleClick: () => {
            props.copy(clientSeed, 'Client seed copied!');
          },
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.nonce,
    id: 'nonce',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Nonce',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.hash,
    id: 'hash',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Hash',
      };
    },
    cell: (cell) => {
      const hash = cell.cell.getValue() as string;
      return flexRenderComponent(TableColorCellComponent, {
        inputs: {
          value: hash,
          maxWidth: 100,
          cursor: 'pointer',
        },
        outputs: {
          handleClick: () => {
            props.copy(hash, 'Hash copied!');
          },
        },
      });
    },
    enableSorting: true,
  },
  {
    id: 'fairness',
    header: () => 'Fairness',
    cell: (cell) =>
      flexRenderComponent(FairnessButtonComponent, {
        inputs: {
          href: `/provably-fair/towers/${cell.row.original.id}`,
        },
        outputs: {
          handleClick: () => {
            props.modalService.closeModals(['MyProfileModal']);
          },
        },
      }),
    enableSorting: false,
  },
];
